import React, { useState, useRef, useEffect, useCallback } from 'react';
import './VideoUploader.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UploadForm from './UploadForm/UploadForm';
import BulkUpload from './BulkUpload/BulkUpload';
import CarouselUploadForm from './CarouselUploadForm';
import { uploadFileToFirebase } from '../../utils/firebase.js';
import { getFileType, getFileTypeFromFirebasePath } from '../../utils/getFileType.js';
import { v4 as uuidv4 } from 'uuid';
import { checkImagePropertie } from '../../utils/metadataCheck.js';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useQuota } from '../../context/QuotaContext';
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const VideoUploader = ({ draft, closeScheduleModal, uploadType, onQuotaRefresh, isUploadLimitReached, quotaLoading }) => {
  const navigate = useNavigate();
  const hasRunOnceRef = useRef(false);

  const [formData, setFormData] = useState({});
  const uniquePostId = uuidv4();
  const { activeWorkspace, idToken } = useWorkspace();
  const { refreshQuota } = useQuota();


  const handleEditDraft = useCallback(async (group) => {
    const updatedFormData = group.posts.map((data) => {
      const post = group.posts.find(post => post.uploadTo === data.uploadTo);
      if (post) {
        const draftScheduleDate = new Date(post.schedule);
        if (draftScheduleDate > new Date()) {
          post.schedule = draftScheduleDate;
        }
        const newFormData = {
          draftId: post._id,
          postId: post.post_id,
          platformId: post.platform_id,
          title: post.title,
          description: post.description,
          privacyStatus: post.privacy_status,
          schedule: post.schedule,
          uploadType: post.uploadType,
          uploadTo: post.uploadTo,
          status: post.status,
          firebasePath: post.firebase_path,
          videoPath: post.video_path,
          hasBeenEdited: true,
          comment: post.comment,
          board_ids: post.board_ids || [],
          link: post.link,
          alt_text: post.alt_text,
          subreddits: post.subreddits || [],
          carouselImages: post.carousel_images || [],
        };

        return newFormData;
      } else {
        return data;
      }
    });
    setFormData(updatedFormData);
  }, []);

  useEffect(() => {
    if (!hasRunOnceRef.current && draft) {
      if (draft.posts.length > 0) {
        handleEditDraft(draft);
      }
      hasRunOnceRef.current = true;
    }
  }, [draft, handleEditDraft]);

  const handleUploadButtonClick = async (uploadData, sharedToastId = null) => {
    if (uploadData.uploadTo === 'draft') return { success: false, platform: uploadData.uploadTo };

    // Check if upload limit is reached
    if (isUploadLimitReached) {
      return { success: false, platform: uploadData.uploadTo, message: 'Upload limit reached' };
    }

    // Check if quota is still loading
    if (quotaLoading) {
      return { success: false, platform: uploadData.uploadTo, message: 'Quota loading' };
    }

    const uploadPromises = [];
    let thumb = formData.thumbnail;
    try {
      if (getFileType(uploadData.uploadType) === 'video' && (!uploadData.videoPath)) {
        return { success: false, platform: uploadData.uploadTo, message: 'No video uploaded' };
      }

      if (getFileType(uploadData.uploadType) === 'image' && (!uploadData.videoPath && uploadData.uploadTo === 'instagram')) {
        return { success: false, platform: uploadData.uploadTo, message: 'Image required' };
      }

      if (thumb && typeof thumb !== 'string') {
        thumb = await uploadFileToFirebase(thumb, 'img', null, activeWorkspace.id);
      }

      if (uploadData.uploadTo) {
        uploadPromises.push(handlePlatformUpload(uploadData));
      }

      // Only create individual toast if no shared toast is provided (single upload)
      let toastId = sharedToastId;
      if (!sharedToastId) {
        toastId = toast.loading('Uploading to ' + uploadData.uploadTo);
      }

      let promises = await Promise.all(uploadPromises);
      let hasSuccessfulUpload = false;
      let result = promises[0];

      if (result && result.success) {
        hasSuccessfulUpload = true;
      }

      // Only update toast if it's a single upload (no shared toast)
      if (!sharedToastId && toastId) {
        toast.update(toastId, {
          render: result.message,
          type: result.success ? 'success' : 'error',
          isLoading: false,
          autoClose: 5000,
        });
      }

      // Refresh quota if any upload was successful
      if (hasSuccessfulUpload) {
        refreshQuota();
        // Also call the legacy callback if provided
        if (onQuotaRefresh) {
          onQuotaRefresh();
        }
      }

      return { success: result.success, platform: uploadData.uploadTo, message: result.message };

    } catch (error) {
      console.error('UPLOAD API ERROR:', error);
      return { success: false, platform: uploadData.uploadTo, message: 'Upload error' };
    }
  };

  const handlePlatformUpload = async (uploadData) => {
    switch (uploadData.uploadTo) {
      case 'youtube':
        return uploadToYoutube(uploadData);
      case 'linkedin':
        return uploadToLinkedin(uploadData);
      case 'tiktok':
        return uploadToTiktok(uploadData);
      case 'instagram':
        return uploadToInstagram(uploadData);
      case 'facebook':
        return uploadToFacebook(uploadData);
      case 'x':
        return uploadToX(uploadData);
      case 'threads':
        return uploadToThreads(uploadData);
      case 'reddit':
        return uploadToReddit(uploadData);
      case 'pinterest':
        return uploadToPinterest(uploadData);
      default:
        return { success: false, message: 'Unsupported platform' };
    }
  };

  const uploadToThreads = async (uploadData) => {
    let mediaType = '';
    if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'video') {
      mediaType = 'VIDEO';
    } else if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'image') {
      mediaType = 'IMAGE';
    }

    if (!uploadData.firebasePath && !formData.videoPath && !uploadData.videoPath) {
      mediaType = 'TEXT';
    }

    const data = {
      id: uploadData.draftId,
      description: uploadData.description,
      platform_id: uploadData.platformId,
      postId: uploadData.postId,
      mediaType: mediaType,
      uploadTo: uploadData.uploadTo,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      uploadType: uploadData.uploadType,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
    };

    try {
      const response = await axios.post(BACKEND_API_URL + '/api/threads/upload', { data }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.status === 201) {
        return {
          success: true,
          message: `Your content has been uploaded to ${uploadData.uploadTo}!`,
        };
      }
    } catch (error) {
      console.error(error);
      handleErrorUpload(data);
      return {
        success: false,
        message: `An error occurred while uploading to ${uploadData.uploadTo}!`,
      };
    }
  };

  const uploadToYoutube = async (uploadData) => {
    if (!uploadData.videoPath) {
      return {
        success: true,
        message: `No video found...`,
      };
    }

    const data = {
      id: uploadData.draftId,
      title: uploadData.title,
      desc: uploadData.description,
      privacyStatus: uploadData.privacyStatus,
      platform_id: uploadData.platformId,
      postId: uploadData.postId,
      uploadTo: uploadData.uploadTo,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      uploadType: uploadData.uploadType,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
    };
    try {
      const response = await axios.post(BACKEND_API_URL + '/api/google/upload_video', { data }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.status === 201)
        return {
          success: true,
          message: `Your content has been uploaded to ${uploadData.uploadTo}!`,
        };
    } catch (error) {
      if (error) {
        handleErrorUpload(data);
        return {
          success: false,
          message: `An error occurred while uploading to ${uploadData.uploadTo}!`,
        };
      }
    }
  };

  const uploadToTiktok = async (uploadData) => {
    let privacy = '';
    if (uploadData.privacyStatus === "private") {
      privacy = "SELF_ONLY"
    } else if (uploadData.privacyStatus === "public") {
      privacy = "PUBLIC_TO_EVERYONE"
    }

    const data = {
      id: uploadData.draftId,
      postId: uploadData.postId,
      description: uploadData.description,
      privacyStatus: privacy,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      platform_id: uploadData.platformId,
      allowComment: uploadData.commentEnabled,
      allowDuet: uploadData.duetEnabled,
      allowStitch: uploadData.stitchEnabled,
      selfBrand: uploadData.isOwnBrand,
      uploadTo: uploadData.uploadTo,
      contentBrand: uploadData.isBranded,
      uploadType: uploadData.uploadType,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
      carousel_images: uploadData.carouselImages || [],
    };
    try {
      let response;

      // Check if this is a carousel upload
      if (uploadData.carouselImages && uploadData.carouselImages.length > 0) {
        response = await axios.post(BACKEND_API_URL + '/api/tiktok/upload_carousel', { data }, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } else if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'image') {
        response = await axios.post(BACKEND_API_URL + '/api/tiktok/init_photo_upload', { data }, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
      } else {
        response = await axios.post(BACKEND_API_URL + '/api/tiktok/init_upload', { data }, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
      }
      if (response.status === 201)
        return {
          success: true,
          message: `Your content has been uploaded to ${uploadData.uploadTo}!`,
        };
    } catch (error) {
      if (error) {
        handleErrorUpload(data);
        return {
          success: false,
          message: `An error occurred while uploading to ${uploadData.uploadTo}!`,
        };
      }
    }
  };

  const uploadToInstagram = async (uploadData) => {
    // Check if it's a carousel upload
    if (uploadData.carouselImages && uploadData.carouselImages.length > 0) {
      // For carousel uploads, no need to check for individual video/image
    } else if (!uploadData.videoPath) {
      return {
        success: true,
        message: `No video found...`,
      };
    }

    let endpoint = '';
    // Check if this is a carousel upload
    if (uploadData.carouselImages && uploadData.carouselImages.length > 0) {
      endpoint = '/api/instagram/upload_carousel';
    } else if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'video') {
      if (uploadData.uploadType === 'STORIES') uploadData.uploadType = 'STORIES'
      else uploadData.uploadType = 'REELS'

      endpoint = '/api/instagram/upload_video'
    } else if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'image') {
      endpoint = '/api/instagram/upload_image'
    }

    const data = {
      id: uploadData.draftId,
      description: uploadData.description,
      platform_id: uploadData.platformId,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      uploadType: uploadData.uploadType,
      postId: uploadData.postId,
      uploadTo: uploadData.uploadTo,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
      carousel_images: uploadData.carouselImages || [],
    };

    try {
      // Skip image validation for carousel uploads (will be handled by backend)
      if (!uploadData.carouselImages || uploadData.carouselImages.length === 0) {
        if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'image') {
          const { aspectRatio, width } = await checkImagePropertie(data.firebasePath || data.video_path);

          if (aspectRatio < 0.8 || aspectRatio > 1.91) {
            return {
              success: false,
              message: `The image aspect ratio for instagram must be between 4:5 (0.8) and 1.91:1 (1.91). Your image has an aspect ratio of ${aspectRatio.toFixed(2)}.`,
            };
          }

          if (width < 320 || width > 1440) {
            return {
              success: false,
              message: `The image width must be between 320px and 1440px. Your image width is ${width}px.`,
            };
          }
        }
      }

      const response = await axios.post(BACKEND_API_URL + endpoint, { data }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.status === 201)
        return {
          success: true,
          message: `Your content has been uploaded to ${uploadData.uploadTo}!`,
        };
    } catch (error) {
      console.error(error);

      if (error) {
        handleErrorUpload(data);
        return {
          success: false,
          message: `An error occurred while uploading to ${uploadData.uploadTo}!`,
        };
      }
    }
  };

  const uploadToFacebook = async (uploadData) => {
    const data = {
      id: uploadData.draftId,
      title: uploadData.title,
      description: uploadData.description,
      platform_id: uploadData.platformId,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      postId: uploadData.postId,
      uploadTo: uploadData.uploadTo,
      uploadType: uploadData.uploadType,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
    };

    let endpoint = '';

    if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'video') {
      if (uploadData.uploadType === 'reel') {
        endpoint = '/api/facebook/upload_reel';




      } else {
        endpoint = '/api/facebook/upload_video';
      }
    }
    else {
      endpoint = '/api/facebook/upload_post';
    }

    try {
      const response = await axios.post(
        BACKEND_API_URL + endpoint,
        { data },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      if (response.status === 201) {
        return {
          success: true,
          message: `Your content has been uploaded to ${uploadData.uploadTo}!`,
        };
      }
    } catch (error) {
      handleErrorUpload(data);
      return {
        success: false,
        message: `An error occurred while uploading to ${uploadData.uploadTo}!`,
      };
    }
  };

  const uploadToLinkedin = async (uploadData) => {
    // Determine if we have media or just text
    let hasMedia = uploadData.videoPath || formData.videoPath || uploadData.firebasePath;
    let mediaType = '';

    // Determine media type based on file extension
    if (hasMedia) {
      const path = uploadData.firebasePath || '';
      if (getFileTypeFromFirebasePath(path) === 'video') {
        mediaType = 'VIDEO';
      } else if (getFileTypeFromFirebasePath(path) === 'image') {
        mediaType = 'IMAGE';
      } else {
        mediaType = 'NONE';
      }
    }

    const data = {
      id: uploadData.draftId,
      description: uploadData.description,
      platform_id: uploadData.platformId,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      uploadType: mediaType || 'NONE',
      postId: uploadData.postId,
      uploadTo: uploadData.uploadTo,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
      text: uploadData.description
    };

    try {
      // Use LinkedIn service to handle all media types
      const response = await axios.post(
        BACKEND_API_URL + '/api/linkedin/share',
        { data },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      if (response.status === 201) {
        return {
          success: true,
          message: `Your content has been shared on LinkedIn!`,
        };
      }
    } catch (error) {
      console.error('LinkedIn upload error:', error);
      handleErrorUpload(data);
      return {
        success: false,
        message: `An error occurred while sharing to LinkedIn!`,
      };
    }
  };

  const uploadToX = async (uploadData) => {
    if (!uploadData.description && !uploadData.videoPath) {
      return {
        success: false,
        message: `No content found for the tweet...`,
      };
    }

    const data = {
      id: uploadData.draftId,
      description: uploadData.description,
      platform_id: uploadData.platformId,
      video_path: uploadData.videoPath || formData.videoPath,
      firebasePath: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      uploadType: uploadData.uploadType,
      postId: uploadData.postId,
      uploadTo: uploadData.uploadTo,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
    };

    try {
      const response = await axios.post(BACKEND_API_URL + '/api/x/upload_post', { data }, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (response.status === 201)
        return {
          success: true,
          message: `Your tweet has been posted to ${uploadData.uploadTo}!`,
        };
    } catch (error) {
      console.error('UPLOAD API ERROR:', error);
      handleErrorUpload(data);
      return {
        success: false,
        message: `An error occurred while posting to ${uploadData.uploadTo}!`,
      };
    }
  };

  const uploadToReddit = async (uploadData) => {
    const subreddits = uploadData.subreddits || [];

    if (subreddits.length === 0) {
      return {
        success: false,
        message: `No subreddits selected for Reddit upload!`,
      };
    }

    const results = [];

    for (const subreddit of subreddits) {
      const data = {
        id: uploadData.draftId,
        title: uploadData.title,
        description: uploadData.description,
        platform_id: uploadData.platformId,
        video_path: uploadData.videoPath || formData.videoPath,
        firebasePath: uploadData.firebasePath || formData.firebasePath,
        thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
        postId: uploadData.postId + '_' + subreddit, // Make unique postId per subreddit
        uploadTo: uploadData.uploadTo,
        uploadType: uploadData.uploadType,
        workspaceId: activeWorkspace.id,
        comment: uploadData.comment,
        subreddit: subreddit,
      };

      let endpoint = '';

      // Determine if it's a video or image/text post
      if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'video') {
        endpoint = '/api/reddit/upload/video';
      } else {
        endpoint = '/api/reddit/upload/post';
      }

      try {
        const response = await axios.post(
          BACKEND_API_URL + endpoint,
          { data },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );

        if (response.status === 201) {
          results.push({
            success: true,
            subreddit: subreddit,
            message: `Uploaded to r/${subreddit}`,
          });
        }
      } catch (error) {
        console.error(`Reddit upload error for r/${subreddit}:`, error);
        handleErrorUpload(data);
        results.push({
          success: false,
          subreddit: subreddit,
          message: `Failed to upload to r/${subreddit}`,
        });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const totalCount = results.length;

    return {
      success: successCount > 0,
      message: `Reddit uploads: ${successCount}/${totalCount} successful. ${results.filter(r => r.success).map(r => r.subreddit).join(', ')}`,
    };
  };

  const uploadToPinterest = async (uploadData) => {
    const boardIds = uploadData.board_ids || [];

    if (boardIds.length === 0) {
      return {
        success: false,
        message: `No boards selected for Pinterest upload!`,
      };
    }

    const results = [];

    for (const boardId of boardIds) {
      const data = {
        id: uploadData.draftId,
        title: uploadData.title,
        description: uploadData.description,
        platform_id: uploadData.platformId,
        video_path: uploadData.videoPath || formData.videoPath,
        firebasePath: uploadData.firebasePath || formData.firebasePath,
        thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
        postId: uploadData.postId + '_' + boardId, // Make unique postId per board
        uploadTo: uploadData.uploadTo,
        uploadType: uploadData.uploadType,
        workspaceId: activeWorkspace.id,
        comment: uploadData.comment,
        board_id: boardId,
        link: uploadData.link,
        alt_text: uploadData.alt_text,
      };

      let endpoint = '';

      // Determine if it's a video or image/text post
      if (getFileTypeFromFirebasePath(uploadData.firebasePath) === 'video') {
        endpoint = '/api/pinterest/upload/video';
      } else {
        endpoint = '/api/pinterest/upload/post';
      }

      try {
        const response = await axios.post(
          BACKEND_API_URL + endpoint,
          { data },
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );

        if (response.status === 201) {
          results.push({
            success: true,
            boardId: boardId,
            message: `Uploaded to board ${boardId}`,
          });
        }
      } catch (error) {
        console.error(`Pinterest upload error for board ${boardId}:`, error);
        handleErrorUpload(data);
        results.push({
          success: false,
          boardId: boardId,
          message: `Failed to upload to board ${boardId}`,
        });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const totalCount = results.length;

    return {
      success: successCount > 0,
      message: `Pinterest uploads: ${successCount}/${totalCount} successful boards.`,
    };
  };

  const handleErrorUpload = async (data) => {
    if (data.platform_id === undefined) return;
    const platform_id = data.platform_id;

    try {
      await axios.post(BACKEND_API_URL + '/api/auth/user/social/error', { platform_id, workspaceId: activeWorkspace.id }, { headers: { Authorization: `Bearer ${idToken}` } });
    } catch (error) {
      console.error(error);
    }
  };

  const saveToDraft = async (isSchedule = false, uploadData, sharedToastId = null) => {
    // Check if upload limit is reached for scheduling (may consume quota)
    if (isSchedule && isUploadLimitReached) {
      return { success: false, platform: uploadData.uploadTo, message: 'Upload limit reached' };
    }

    // Check if quota is still loading for scheduling
    if (isSchedule && quotaLoading) {
      return { success: false, platform: uploadData.uploadTo, message: 'Quota loading' };
    }

    let thumb = uploadData.thumbnail;

    if (thumb && typeof thumb !== 'string') {
      thumb = await uploadFileToFirebase(uploadData.thumbnail, 'img', null, activeWorkspace.id);
    }
    if (uploadData.status === 'uploaded') {
      uploadData.postId = uniquePostId;
      uploadData.draftId = null;
    }
    const data = {
      id: uploadData.draftId || undefined,
      post_id: uploadData.postId,
      platform_id: uploadData.platformId,
      title: uploadData.title,
      description: uploadData.description,
      uploadType: uploadData.uploadType || '',
      privacy_status: uploadData.privacyStatus || 'private',
      video_path: uploadData.videoPath || formData.videoPath,
      firebase_path: uploadData.firebasePath || formData.firebasePath,
      thumbnail: uploadData.thumbnailPath || uploadData.thumbnail,
      status: isSchedule ? 'scheduled' : 'draft',
      uploadTo: uploadData.uploadTo,
      schedule: isSchedule ? uploadData.schedule : null,
      workspaceId: activeWorkspace.id,
      comment: uploadData.comment,
      // Pinterest-specific fields
      board_ids: uploadData.board_ids || [],
      link: uploadData.link,
      alt_text: uploadData.alt_text,
      // Reddit-specific fields
      subreddits: uploadData.subreddits || [],
      // Carousel-specific fields
      carousel_images: uploadData.carouselImages || [],
    };

    try {
      await axios.post(BACKEND_API_URL + '/api/auth/user/drafts', { data }, { headers: { Authorization: `Bearer ${idToken}` } });
      
      // Only show individual toast if no shared toast is provided
      if (!sharedToastId) {
        toast.success(isSchedule ? 'Your post has been scheduled!' : 'Your post has been saved to your drafts!');
      }

      if (onQuotaRefresh) {
        onQuotaRefresh();
      }

      if (!sharedToastId) {
        if (isSchedule && typeof closeScheduleModal === 'function') {
          closeScheduleModal();
        } else {
          navigate('/dashboard');
        }
      }

      return { success: true, platform: uploadData.uploadTo, message: isSchedule ? 'Scheduled' : 'Saved to drafts' };
    } catch (error) {
      // Only show individual error toast if no shared toast
      if (!sharedToastId) {
        toast.error(isSchedule ? 'Failed to schedule your post, please try again!' : 'Failed to save your post to drafts, please try again!');
      }
      return { success: false, platform: uploadData.uploadTo, message: isSchedule ? 'Failed to schedule' : 'Failed to save' };
    }
  };

  const handleScheduleUpload = async (uploadData, sharedToastId = null) => {
    if (uploadData.uploadTo === 'draft') return { success: false, platform: uploadData.uploadTo };
    return await saveToDraft(true, uploadData, sharedToastId);
  };

  const handleFormAction = async (action, data) => {
    if (data.length === 0) {
      toast.warning('There has been no changed made!');
      return;
    }

    // For multiple items, create a consolidated toast
    if (data.length > 1) {
      const platforms = [...new Set(data.map(d => d.uploadTo).filter(p => p !== 'draft'))];
      const platformNames = platforms.join(', ');
      let toastId;
      
      // Create appropriate loading message based on action
      if (action === 'upload') {
        toastId = toast.loading(`Uploading ${data.length} video${data.length > 1 ? 's' : ''} to ${platformNames}`);
      } else if (action === 'schedule') {
        toastId = toast.loading(`Scheduling ${data.length} post${data.length > 1 ? 's' : ''} for ${platformNames}`);
      } else if (action === 'draft') {
        toastId = toast.loading(`Saving ${data.length} post${data.length > 1 ? 's' : ''} to drafts`);
      }

      // Process all platforms with shared toast
      let promises;
      if (action === 'upload') {
        promises = data.map(platform => handleUploadButtonClick(platform, toastId));
      } else if (action === 'schedule') {
        promises = data.map(platform => handleScheduleUpload(platform, toastId));
      } else if (action === 'draft') {
        promises = data.map(platform => saveToDraft(false, platform, toastId));
      }
      
      const results = await Promise.all(promises);

      // Count successes and failures
      const successful = results.filter(r => r && r.success).length;
      const failed = results.filter(r => r && !r.success).length;

      // Update the consolidated toast with final results based on action
      if (successful > 0 && failed === 0) {
        let successMessage;
        if (action === 'upload') {
          successMessage = `Successfully uploaded ${successful} video${successful > 1 ? 's' : ''} to ${platformNames}`;
        } else if (action === 'schedule') {
          successMessage = `Successfully scheduled ${successful} post${successful > 1 ? 's' : ''} for ${platformNames}`;
        } else if (action === 'draft') {
          successMessage = `Successfully saved ${successful} post${successful > 1 ? 's' : ''} to drafts`;
        }
        
        toast.update(toastId, {
          render: successMessage,
          type: 'success',
          isLoading: false,
          autoClose: 5000,
        });
      } else if (successful > 0 && failed > 0) {
        let warningMessage;
        if (action === 'upload') {
          warningMessage = `Uploaded ${successful} video${successful > 1 ? 's' : ''}, ${failed} failed`;
        } else if (action === 'schedule') {
          warningMessage = `Scheduled ${successful} post${successful > 1 ? 's' : ''}, ${failed} failed`;
        } else if (action === 'draft') {
          warningMessage = `Saved ${successful} post${successful > 1 ? 's' : ''} to drafts, ${failed} failed`;
        }
        
        toast.update(toastId, {
          render: warningMessage,
          type: 'warning',
          isLoading: false,
          autoClose: 5000,
        });
      } else {
        let errorMessage;
        if (action === 'upload') {
          errorMessage = 'Failed to upload videos. Please try again.';
        } else if (action === 'schedule') {
          errorMessage = 'Failed to schedule posts. Please try again.';
        } else if (action === 'draft') {
          errorMessage = 'Failed to save posts to drafts. Please try again.';
        }
        
        toast.update(toastId, {
          render: errorMessage,
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
      }

      // Navigate after all operations complete
      if (action === 'upload') {
        navigate('/dashboard');
      } else if (action === 'schedule') {
        if (typeof closeScheduleModal === 'function') {
          closeScheduleModal();
        } else {
          navigate('/dashboard');
        }
      } else if (action === 'draft') {
        navigate('/dashboard');
      }
    } else {
      // Process single item normally
      const promises = [];
      for (const platform of data) {
        if (action === 'upload') {
          promises.push(handleUploadButtonClick(platform));
        } else if (action === 'schedule') {
          promises.push(handleScheduleUpload(platform));
        } else if (action === 'draft') {
          promises.push(saveToDraft(false, platform));
        }
      }
      // Wait for all operations to complete
      await Promise.all(promises);
      
      // Navigate for single items
      if (action === 'upload' && data.length === 1) {
        navigate('/dashboard');
      }
    }
  };


  return (
    <div className='flex flex-col w-full h-full overflow-hidden'>
      {uploadType === 'bulk' ? (
        <BulkUpload
          onFormAction={handleFormAction}
          isUploadLimitReached={isUploadLimitReached}
          quotaLoading={quotaLoading}
        />
      ) : uploadType === 'carousel' ? (
        <CarouselUploadForm
          draftFormData={formData}
          onFormAction={handleFormAction}
          onQuotaRefresh={onQuotaRefresh}
          isUploadLimitReached={isUploadLimitReached}
          quotaLoading={quotaLoading}
        />
      ) : (
        <UploadForm
          draftFormData={formData}
          onFormAction={handleFormAction}
          uploadType={uploadType}
        />
      )}
    </div>
  );
};

export default VideoUploader;